import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { useHandicap } from '../../context/HandicapContext';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import DetailedPlayerVoteModal from './DetailedPlayerVoteModal';
import BreakdownIcon from '../icons/BreakdownIcon';
import { deadHeatShareFromMax, deadHeatPlaceShare, countAtMax, formatWins, formatPct } from '../../utils/deadHeat';
import ImpliedOddsInfo from '../common/ImpliedOddsInfo';
// Individual count sources
// No static game vote data imports. All vote data comes from API.

// Caret shown in a sortable header when that column is the active sort key.
const SortCaret = () => (
  <svg className="inline-block w-2.5 h-2.5 ml-1 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
  </svg>
);

// Marks a per-source cell where the player leads that dataset.
const LeaderCheck = ({ className = 'w-2.5 h-2.5 mt-0.5' }) => (
  <svg className={`${className} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronDown = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
  </svg>
);

// Podium emphasis for the position column: emerald for the leader, a raised
// slate chip for the rest of the top three, bare numerals below that.
const rankBadgeClass = (rank) => {
  if (rank === 1) return 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300';
  if (rank === 2 || rank === 3) return 'bg-slate-800/80 border-slate-700/70 text-slate-200';
  return 'border-transparent text-slate-500';
};

// The frozen POS/PLAYER columns need an opaque surface so scrolled data cannot
// show through, and have to track the row hover tint themselves. PLAYER pins a
// couple of pixels under the POS column width so the two frozen cells always
// overlap into POS's trailing padding rather than leaving a seam to bleed through.
// The row group is named so it cannot trigger the unnamed popover groups nested
// inside the vote-frequency cells.
const STICKY_CELL = 'md:sticky md:z-10 bg-[#0a1120] group-hover/row:bg-[#121b2b] transition-colors';
const STICKY_HEAD = 'md:sticky md:z-20 bg-[#0e1625]';

const PlayerLeaderboardTable = ({ searchTerm = '', filterFrom = null, filterTo = null, leaderboardMode = 'votes', voteFrequencyEnabled = false, freqThresholds, voteDataRemote = null, handicapEnabled = false }) => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortKey, setSortKey] = useState('votes'); // 'votes' | 'count1'|'count2'|'count3'|'count4'|'count5'|'betfair'|'espn'|'byb'
  const [xplusHover, setXplusHover] = useState({ name: null, threshold: null });
  const [expandedMobileRows, setExpandedMobileRows] = useState({});

  // Enabled dataset keys (count1..count5, betfair, espn, byb)
  const { enabledDatasets, getEnabledKeys } = useSettings();
  
  // Handicap context functions
  const {
    updateTempHandicapInput,
    applyPlayerHandicap,
    getPlayerHandicap,
    getDisplayHandicapValue,
    resetTeamHandicaps
  } = useHandicap();
  const enabledSourceKeys = getEnabledKeys();
  const enabledSourceSet = new Set(enabledSourceKeys);
  const allSourcesOrder = ['byb','espn','betfair','count1','count2','count3','count4','count5'];
  const enabledIndices = allSourcesOrder
    .map((k, idx) => (enabledSourceSet.has(k) ? idx : -1))
    .filter((idx) => idx >= 0);
  // Display order for tables (C1..C5 then external)
  const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
  const enabledDisplayKeys = displayOrder.filter((k) => enabledSourceSet.has(k));
  const keyToLabel = { count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', betfair: 'BETFAIR', espn: 'ESPN', byb: 'BYB' };

  const averageEnabledFromRow = (rowObj) => {
    const keys = enabledDisplayKeys;
    const n = keys.length;
    if (!n) return 0;
    let sum = 0;
    const playerName = rowObj?.name;
    const handicap = handicapEnabled && playerName ? getPlayerHandicap('leaderboard', playerName) : 0;
    
    for (const k of keys) {
      const v = Number(rowObj?.[k] ?? 0);
      sum += isNaN(v) ? 0 : v;
    }
    return (sum / n) + handicap;
  };

  // Finish/X+ modes put their extra columns inside the expanded panel, so on
  // mobile rows default to open in those modes. Users can still collapse
  // individual rows; switching modes clears those manual overrides.
  const mobileAutoExpand = leaderboardMode === 'finish' || leaderboardMode === 'xplus';

  useEffect(() => {
    setExpandedMobileRows({});
  }, [leaderboardMode]);

  const toggleMobileRow = (playerName) => {
    setExpandedMobileRows((prev) => ({ ...prev, [playerName]: !(prev[playerName] ?? mobileAutoExpand) }));
  };

  // Function to get player image based on name
  const getPlayerImage = (playerName) => {
    const player = aflPlayerData.find(p => p.player_name === playerName);
    return player ? player.image_src : null;
  };

  // Filter players based on search term from parent
  const normalizedSearch = searchTerm.toLowerCase();
  const filteredPlayers = useMemo(() => {
    const s = normalizedSearch;
    const source = voteDataRemote?.leaderboard || [];
    return source.filter(
      (player) => player.name.toLowerCase().includes(s) || player.team.toLowerCase().includes(s)
    );
  }, [normalizedSearch, voteDataRemote]);

  // Open modal with selected player
  const openPlayerModal = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  // Recompute leaderboard values per filter range
  const applyRangeTotal = (sourceTotals, playerName) => {
    const key = playerName.toLowerCase();
    let sum = 0;
    const from = filterFrom ?? 0;
    const to = filterTo ?? 24;
    for (let r = from; r <= to; r++) {
      const perRound = sourceTotals.get(key) || {};
      sum += perRound[String(r)] || 0;
    }
    return sum;
  };

  const normalize = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const PLAYER_NAME_TO_TEAM = useMemo(() => {
    const map = new Map();
    const source = voteDataRemote?.leaderboard || [];
    for (const p of source) {
      map.set(p.name.toLowerCase(), (p.full_team_name || p.team || '').toString());
    }
    return map;
  }, [voteDataRemote]);

  const buildPerPlayerRoundTotalsFrom = (dataSet) => {
    const result = new Map();
    const data = dataSet && dataSet.game_vote_data ? dataSet.game_vote_data : [];
    for (const roundData of data) {
      const round = String(roundData.round);
      for (const game of roundData.game_votes) {
        for (const vote of game.votes) {
          const key = vote.player.toLowerCase();
          if (!PLAYER_NAME_TO_TEAM.has(key)) continue;
          const expectedTeam = normalize(PLAYER_NAME_TO_TEAM.get(key));
          if (normalize(vote.team) !== expectedTeam) continue;
          if (!result.has(key)) result.set(key, {});
          const r = result.get(key);
          r[round] = (r[round] || 0) + vote.votes;
        }
      }
    }
    return result;
  };

  // Ensure team map reflects current remote leaderboard
  const playersSource = voteDataRemote?.leaderboard || [];
  const teamMap = useMemo(() => {
    const map = new Map();
    for (const p of playersSource) {
      map.set(p.name.toLowerCase(), (p.full_team_name || p.team || '').toString());
    }
    return map;
  }, [playersSource]);
  if (teamMap.size) {
    PLAYER_NAME_TO_TEAM.clear();
    teamMap.forEach((v, k) => PLAYER_NAME_TO_TEAM.set(k, v));
  }

  const SOURCE_TOTALS_LIST = useMemo(() => {
    const wrap = (d) => (Array.isArray(d) ? { game_vote_data: d } : d);
    if (!voteDataRemote) return [];
    return [
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_byb)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_espn)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_betfair)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_1)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_2)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_3)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_4)),
      buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_5)),
    ];
  }, [voteDataRemote]);

  // Combined model totals from main dataset for overall VOTES column
  const MAIN_TOTALS = useMemo(() => {
    const wrap = (d) => (Array.isArray(d) ? { game_vote_data: d } : d);
    if (!voteDataRemote) return new Map();
    return buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data));
  }, [voteDataRemote]);

  const getMainRowTotal = (playerName) => {
    const key = playerName.toLowerCase();
    const from = filterFrom ?? 0;
    const to = filterTo ?? 24;
    let sum = 0;
    for (let r = from; r <= to; r++) {
      const perRound = MAIN_TOTALS.get(key) || {};
      sum += perRound[String(r)] || 0;
    }
    return sum;
  };

  const filteredLeaderboard = useMemo(() => {
    const source = voteDataRemote?.leaderboard || [];
    const from = filterFrom ?? 0;
    const to = filterTo ?? 24;
    return source.map((p) => {
      if (filterFrom === null && filterTo === null) {
        // Unfiltered: keep source values but override votes as dynamic average of enabled datasets
        return { ...p, votes: averageEnabledFromRow(p) };
      }
      const name = p.name;
      const [byb, espn, betfair, c1, c2, c3, c4, c5] = SOURCE_TOTALS_LIST.map((src) => applyRangeTotal(src, name));
      const tempRow = { ...p, byb, espn, betfair, count1: c1, count2: c2, count3: c3, count4: c4, count5: c5 };
      const votes = averageEnabledFromRow(tempRow);
      return {
        ...p,
        byb,
        espn,
        betfair,
        count1: c1,
        count2: c2,
        count3: c3,
        count4: c4,
        count5: c5,
        votes,
      };
    });
  }, [filterFrom, filterTo, SOURCE_TOTALS_LIST, enabledSourceKeys, voteDataRemote]);

  // Vote Frequency precompute (independent of round filtering; always uses rounds 0..24)
  const FREQ_STATS = useMemo(() => {
    if (!voteFrequencyEnabled) return null;
    const thr = {
      polledMin: (freqThresholds?.polledMin ?? 0.7),
      threeMin: (freqThresholds?.threeMin ?? 2.4),
      twoMin: (freqThresholds?.twoMin ?? 1.4),
      twoMax: (freqThresholds?.twoMax ?? 2.4),
      oneMin: (freqThresholds?.oneMin ?? 0.7),
      oneMax: (freqThresholds?.oneMax ?? 1.4),
    };
    const players = (voteDataRemote?.leaderboard || []).map(p => p.name.toLowerCase());
    const categories = ['polled', 'three', 'two', 'one'];
    const meets = (v, cat) => {
      switch (cat) {
        case 'polled': return v >= thr.polledMin;
        case 'three': return v >= thr.threeMin;
        case 'two': return v >= thr.twoMin && v < thr.twoMax;
        case 'one': return v >= thr.oneMin && v < thr.oneMax;
        default: return false;
      }
    };
    const result = {
      polled: [],
      three: [],
      two: [],
      one: [],
    };
    for (let si = 0; si < SOURCE_TOTALS_LIST.length; si++) {
      const sourceTotals = SOURCE_TOTALS_LIST[si];
      // Build frequency per player for this source
      const freqByPlayer = new Map();
      let maxPolled = 0, maxThree = 0, maxTwo = 0, maxOne = 0;
      for (const playerKey of players) {
        const perRound = sourceTotals.get(playerKey) || {};
        let cPolled = 0, cThree = 0, cTwo = 0, cOne = 0;
        for (let r = 0; r <= 24; r++) {
          const v = perRound[String(r)] || 0;
          if (meets(v, 'polled')) cPolled++;
          if (meets(v, 'three')) cThree++;
          if (meets(v, 'two')) cTwo++;
          if (meets(v, 'one')) cOne++;
        }
        freqByPlayer.set(playerKey, { polled: cPolled, three: cThree, two: cTwo, one: cOne });
        if (cPolled > maxPolled) maxPolled = cPolled;
        if (cThree > maxThree) maxThree = cThree;
        if (cTwo > maxTwo) maxTwo = cTwo;
        if (cOne > maxOne) maxOne = cOne;
      }
      result.polled.push({ freqByPlayer, max: maxPolled });
      result.three.push({ freqByPlayer, max: maxThree });
      result.two.push({ freqByPlayer, max: maxTwo });
      result.one.push({ freqByPlayer, max: maxOne });
    }
    return result;
  }, [voteFrequencyEnabled, freqThresholds, SOURCE_TOTALS_LIST]);

  // Build finishing position maps for each metric (used for finish mode and Top N columns)
  const RANKINGS = useMemo(() => {

    // Helper: build rank map from an array of { name, value }
    const buildRankMap = (items) => {
      const arr = [...items].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
      const valueCounts = new Map();
      for (const it of arr) {
        const v = it.value ?? 0;
        valueCounts.set(v, (valueCounts.get(v) || 0) + 1);
      }
      const map = new Map();
      let rank = 0;
      let prevVal = undefined;
      for (let i = 0; i < arr.length; i++) {
        const { name, value } = arr[i];
        if (i === 0 || value !== prevVal) {
          rank = i + 1;
          prevVal = value;
        }
        const countAtVal = valueCounts.get(value ?? 0) || 1;
        // tieCount sizes the dead-heat split for Top-N place shares.
        map.set(name.toLowerCase(), { rank, isTie: countAtVal > 1, tieCount: countAtVal });
      }
      return map;
    };

    // All players with per-source values (filteredLeaderboard always covers the full list)
    const rows = filteredLeaderboard;
    const makeItems = (key) => rows.map((p) => ({ name: p.name, value: p[key] ?? 0 }));

    const count1 = buildRankMap(makeItems('count1'));
    const count2 = buildRankMap(makeItems('count2'));
    const count3 = buildRankMap(makeItems('count3'));
    const count4 = buildRankMap(makeItems('count4'));
    const count5 = buildRankMap(makeItems('count5'));
    const betfair = buildRankMap(makeItems('betfair'));
    const espn = buildRankMap(makeItems('espn'));
    const byb = buildRankMap(makeItems('byb'));

    // Votes: derive from MAIN_TOTALS within current range for all players
    const voteSource = voteDataRemote?.leaderboard || [];
    const voteItems = voteSource.map((p) => ({
      name: p.name,
      value: getMainRowTotal(p.name),
    }));
    const votes = buildRankMap(voteItems);

    return { count1, count2, count3, count4, count5, betfair, espn, byb, votes };
  }, [filteredLeaderboard, filterFrom, filterTo, MAIN_TOTALS]);

  // Precompute per-source totals and leaders for current range
  const sourceLeaders = useMemo(() => {
    if (filterFrom === null && filterTo === null) return null;
    const from = filterFrom ?? 0;
    const to = filterTo ?? 24;
    return SOURCE_TOTALS_LIST.map((sourceTotals) => {
      let maxTotal = -Infinity;
      const totals = new Map();
      const playersArr = voteDataRemote?.leaderboard || [];
      for (const p of playersArr) {
        const key = p.name.toLowerCase();
        let sum = 0;
        for (let r = from; r <= to; r++) {
          const perRound = sourceTotals.get(key) || {};
          sum += perRound[String(r)] || 0;
        }
        totals.set(key, sum);
        if (sum > maxTotal) maxTotal = sum;
      }
      // Number of players sharing the top total: drives dead-heat win shares.
      const leaderCount = countAtMax(totals.values(), maxTotal);
      return { totals, maxTotal, leaderCount };
    });
  }, [filterFrom, filterTo, SOURCE_TOTALS_LIST]);

  // Get row data for a player (filtered view rows or fallback to original object)
  const getRowForPlayer = (player) => {
    return filteredLeaderboard.find((p) => p.name === player.name) || player;
  };

  const getSortValue = (player) => {
    const row = getRowForPlayer(player);
    switch (sortKey) {
      case 'vf_gp': {
        if (!FREQ_STATS) return 0;
        const lower = player.name.toLowerCase();
        const arr = FREQ_STATS.polled;
        const ordered = [3,4,5,6,7,0,1,2].map((i) => arr[i]);
        let total = 0; let denom = 0;
        for (const src of ordered) {
          const rec = src.freqByPlayer.get(lower);
          if (rec) {
            total += rec.polled || 0; denom += 1;
          }
        }
        return denom > 0 ? total / denom : 0;
      }
      case 'vf_3v': {
        if (!FREQ_STATS) return 0;
        const lower = player.name.toLowerCase();
        const arr = FREQ_STATS.three;
        const ordered = [3,4,5,6,7,0,1,2].map((i) => arr[i]);
        let total = 0; let denom = 0;
        for (const src of ordered) {
          const rec = src.freqByPlayer.get(lower);
          if (rec) { total += rec.three || 0; denom += 1; }
        }
        return denom > 0 ? total / denom : 0;
      }
      case 'vf_2v': {
        if (!FREQ_STATS) return 0;
        const lower = player.name.toLowerCase();
        const arr = FREQ_STATS.two;
        const ordered = [3,4,5,6,7,0,1,2].map((i) => arr[i]);
        let total = 0; let denom = 0;
        for (const src of ordered) {
          const rec = src.freqByPlayer.get(lower);
          if (rec) { total += rec.two || 0; denom += 1; }
        }
        return denom > 0 ? total / denom : 0;
      }
      case 'vf_1v': {
        if (!FREQ_STATS) return 0;
        const lower = player.name.toLowerCase();
        const arr = FREQ_STATS.one;
        const ordered = [3,4,5,6,7,0,1,2].map((i) => arr[i]);
        let total = 0; let denom = 0;
        for (const src of ordered) {
          const rec = src.freqByPlayer.get(lower);
          if (rec) { total += rec.one || 0; denom += 1; }
        }
        return denom > 0 ? total / denom : 0;
      }
      case 'count1':
        return (row.count1 ?? player.count1) ?? 0;
      case 'count2':
        return (row.count2 ?? player.count2) ?? 0;
      case 'count3':
        return (row.count3 ?? player.count3) ?? 0;
      case 'count4':
        return (row.count4 ?? player.count4) ?? 0;
      case 'count5':
        return (row.count5 ?? player.count5) ?? 0;
      case 'betfair':
        return (row.betfair ?? player.betfair) ?? 0;
      case 'espn':
        return (row.espn ?? player.espn) ?? 0;
      case 'byb':
        return (row.byb ?? player.byb) ?? 0;
      case 'votes':
      default:
        // Use dynamic average of enabled datasets for sorting
        return averageEnabledFromRow(row);
    }
  };

  const sortedPlayers = useMemo(() => {
    const arr = [...filteredPlayers];
    // Votes mode: higher is better; Finish mode on votes column: lower position is better
    arr.sort((a, b) => {
      if (leaderboardMode === 'finish' && sortKey === 'votes') {
        // derive finishing positions based on current vote totals
        const ta = averageEnabledFromRow(getRowForPlayer(a));
        const tb = averageEnabledFromRow(getRowForPlayer(b));
        if (tb !== ta) return tb - ta; // sort by value first
        // same value → tie; stable by original position
        return (a.position ?? 0) - (b.position ?? 0);
      }
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (vb !== va) return vb - va; // desc
      return (a.position ?? 0) - (b.position ?? 0);
    });
    return arr;
  }, [filteredPlayers, sortKey, filterFrom, filterTo, leaderboardMode, enabledSourceKeys]);

  // Global ranking map (not affected by search). Uses the same sorting rules as sortedPlayers,
  // but operates over the full player list so POS remains stable when searching.
  // Rank strictly by projected votes (dynamic average of enabled datasets),
  // using the full leaderboard (not affected by text search). If a round
  // filter is active, rankings reflect the filtered range totals.
  const votesRankByName = useMemo(() => {
    const source = voteDataRemote?.leaderboard || [];
    // Use filteredLeaderboard (which always maps across the full source) to get votes
    const rows = filteredLeaderboard.length ? filteredLeaderboard : source.map((p) => ({ ...p, votes: averageEnabledFromRow(p) }));
    const sorted = [...rows].sort((a, b) => {
      const vb = Number(b.votes || 0);
      const va = Number(a.votes || 0);
      if (vb !== va) return vb - va;
      // Tie-break by original position to keep stable ordering
      return (a.position ?? 0) - (b.position ?? 0);
    });
    const map = new Map();
    for (let i = 0; i < sorted.length; i++) {
      map.set((sorted[i].name || '').toLowerCase(), i + 1);
    }
    return map;
  }, [voteDataRemote, filteredLeaderboard, enabledSourceKeys, filterFrom, filterTo]);

  const isFiltered = useMemo(() => filterFrom !== null || filterTo !== null, [filterFrom, filterTo]);

  const isLeaderForSource = (playerName, sourceIdx) => {
    if (!isFiltered || !sourceLeaders) return false;
    const key = playerName.toLowerCase();
    const { totals, maxTotal } = sourceLeaders[sourceIdx] || {};
    if (!totals || maxTotal === undefined || maxTotal <= 0) return false;
    const val = totals.get(key) || 0;
    return val === maxTotal;
  };

  // Highlights use an inset ring rather than a border so tinted cells never
  // shift the column grid by a pixel.
  const leaderCellClass = (flag) => (flag ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30' : '');
  const finishBgClass = (rank) => {
    if (!rank || !rank.rank) return '';
    const r = rank.rank;
    if (r <= 3) return 'bg-emerald-600/25 ring-1 ring-inset ring-emerald-500/30';
    if (r <= 5) return 'bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/20';
    if (r <= 10) return 'bg-amber-400/10 ring-1 ring-inset ring-amber-400/20';
    if (r <= 20) return 'bg-sky-500/10 ring-1 ring-inset ring-sky-500/20';
    return '';
  };

  // Calculate leaders with handicaps when handicap mode is enabled
  const handicapLeaders = useMemo(() => {
    if (!handicapEnabled) return null;
    
    const players = filteredLeaderboard.length ? filteredLeaderboard : (voteDataRemote?.leaderboard || []);
    const leaders = {};
    
    // Calculate leaders for each enabled dataset
    for (const key of enabledDisplayKeys) {
      let maxValue = -Infinity;
      const values = new Map();
      
      for (const player of players) {
        const baseValue = player[key] ?? 0;
        const handicap = getPlayerHandicap('leaderboard', player.name);
        const adjustedValue = baseValue + handicap;
        values.set(player.name.toLowerCase(), adjustedValue);
        
        if (adjustedValue > maxValue) {
          maxValue = adjustedValue;
        }
      }
      
      leaders[key] = { values, maxValue, leaderCount: countAtMax(values.values(), maxValue) };
    }
    
    return leaders;
  }, [handicapEnabled, filteredLeaderboard, voteDataRemote, enabledDisplayKeys, getPlayerHandicap]);

  // Check if a player is leading in a dataset with handicap adjustments
  const isHandicapLeader = (playerName, datasetKey) => {
    if (!handicapEnabled || !handicapLeaders) return false;
    const leader = handicapLeaders[datasetKey];
    if (!leader) return false;
    
    const playerValue = leader.values.get(playerName.toLowerCase()) || 0;
    return playerValue === leader.maxValue && leader.maxValue > 0;
  };

  // Dead-heat win share (1 outright, 1/n for an n-way tie, else 0) for a
  // player in one dataset. Used for implied odds; highlighting still uses the
  // boolean leader checks above.
  const handicapWinShare = (playerName, datasetKey) => {
    if (!handicapEnabled || !handicapLeaders) return 0;
    const leader = handicapLeaders[datasetKey];
    if (!leader) return 0;
    const playerValue = leader.values.get(playerName.toLowerCase()) || 0;
    return deadHeatShareFromMax(playerValue, leader.maxValue, leader.leaderCount);
  };

  const filteredWinShare = (playerName, sourceIdx) => {
    const pack = sourceLeaders?.[sourceIdx];
    if (!pack) return 0;
    const val = pack.totals.get(playerName.toLowerCase()) || 0;
    return deadHeatShareFromMax(val, pack.maxTotal, pack.leaderCount);
  };

  // Effective (dead-heat adjusted) wins across the enabled datasets.
  const computeLeaderHits = (playerName) => {
    let hits = 0;
    if (handicapEnabled) {
      for (const key of enabledDisplayKeys) hits += handicapWinShare(playerName, key);
    } else if (isFiltered && sourceLeaders) {
      for (const idx of enabledIndices) hits += filteredWinShare(playerName, idx);
    }
    return hits;
  };

  return (
    <div>

      {/* Mobile: simplified table (POS, PLAYER, VOTES) with expandable rows */}
      <div className="md:hidden rounded-2xl border border-slate-800 bg-[#0a1120] overflow-hidden">
        <div className="overflow-hidden">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-12" />
              <col className={handicapEnabled ? "w-[40%]" : "w-[58%]"} />
              {handicapEnabled && <col className="w-[18%]" />}
              <col className="w-[30%]" />
            </colgroup>
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className="w-12 text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">POS</th>
                <th className={`${handicapEnabled ? "w-[40%]" : "w-[58%]"} text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]`}>PLAYER</th>
                {handicapEnabled && (
                  <th className="w-[18%] text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">HANDICAP</th>
                )}
                <th className="w-[30%] text-center pl-2 pr-4 py-2.5 text-[10px] font-mono font-semibold text-emerald-300 uppercase tracking-[0.12em]">VOTES</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player, index) => {
                const playerImageSrc = getPlayerImage(player.name);
                const row = getRowForPlayer(player);
                // Same rank source as the desktop table: recomputed from the
                // currently enabled datasets, not the static all-dataset position.
                const displayPosition = votesRankByName.get(player.name.toLowerCase()) ?? player.position;
                const isExpanded = expandedMobileRows[player.name] ?? mobileAutoExpand;
                // votes value for this row (dynamic average of enabled datasets)
                const votesVal = averageEnabledFromRow(row).toFixed(2);
                return (
                  <React.Fragment key={player.name}>
                    <tr className={`border-b border-slate-800/50 ${isExpanded ? 'bg-slate-800/30' : ''}`}>
                      <td className="w-12 pl-2 pr-2 py-2 align-top">
                        <span className={`inline-flex items-center justify-center min-w-[24px] h-[20px] px-1 rounded-md border text-[11px] font-mono font-semibold tabular-nums ${rankBadgeClass(displayPosition)}`}>
                          {displayPosition}
                        </span>
                      </td>
                      <td className={`${handicapEnabled ? "w-[40%]" : "w-[58%]"} pl-2 pr-2 py-2 align-top`}>
                        <div className="flex items-center space-x-2 min-w-0">
                          {playerImageSrc ? (
                            <img src={playerImageSrc} alt={player.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60" />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                              <span className="text-white font-bold text-[10px]">{player.image}</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div
                              className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs cursor-pointer hover:text-emerald-300"
                              onClick={() => openPlayerModal(player)}
                              title="View vote breakdown"
                            >
                              <span className="truncate">{player.name}</span>
                              <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                            </div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">{player.team}</div>
                          </div>
                        </div>
                      </td>
                      {handicapEnabled && (
                        <td className="w-[18%] px-1 py-2 align-top text-center">
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            placeholder="0.0"
                            value={getDisplayHandicapValue('leaderboard', player.name)}
                            onChange={(e) => updateTempHandicapInput('leaderboard', player.name, e.target.value)}
                            onBlur={(e) => applyPlayerHandicap('leaderboard', player.name, e.target.value)}
                            className="w-full bg-slate-950/60 border border-slate-700/60 text-white font-mono tabular-nums rounded-md text-xs px-1 py-1 text-center focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 transition-colors"
                          />
                        </td>
                      )}
                      <td className="w-[30%] pl-2 pr-4 py-2 align-top text-right">
                        <div className="inline-flex items-center justify-end gap-2 pr-1">
                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">{votesVal}</span>
                          <button
                            className={`w-7 h-7 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${isExpanded ? 'rotate-180 border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-slate-700/60 bg-slate-800/40 text-slate-400'}`}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                            onClick={() => toggleMobileRow(player.name)}
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-[#060b16] border-b border-slate-800/50">
                        <td colSpan={handicapEnabled ? 4 : 3} className="px-0 pb-0">
                          <div className="overflow-x-auto styled-scrollbar">
                            <table className="min-w-[720px] w-full">
                              <thead>
                                <tr className="bg-slate-800/20 border-b border-slate-800/60">
                                  {/* Per-source headers (enabled only) */}
                                  {enabledDisplayKeys.map((k) => (
                                    <th key={`h-${k}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">{keyToLabel[k]}</th>
                                  ))}
                                  {leaderboardMode === 'finish' && (
                                    <>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Top 3<ImpliedOddsInfo /></span></th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Top 5<ImpliedOddsInfo /></span></th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Top 10<ImpliedOddsInfo /></span></th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Top 20<ImpliedOddsInfo /></span></th>
                                    </>
                                  )}
                                  {leaderboardMode === 'xplus' && (
                                    <>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">30+ Votes</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">25+ Votes</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">20+ Votes</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">15+ Votes</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">10+ Votes</th>
                                    </>
                                  )}
                                  {isFiltered && (
                                    <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                                  )}
                                  {handicapEnabled && (
                                    <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Handicap Odds<ImpliedOddsInfo /></span></th>
                                  )}
                                  {voteFrequencyEnabled && (
                                    <>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">3V</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">2V</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">1V</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">GP</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {/* Per-source values (enabled only). In finish mode these
                                      show the finishing rank per source, same as desktop —
                                      not the raw vote totals. */}
                                  {enabledDisplayKeys.map((key) => (
                                    <td key={`v-${key}`} className={`text-center px-2 py-2 text-[11px] text-slate-300 font-mono tabular-nums whitespace-nowrap ${handicapEnabled ? leaderCellClass(isHandicapLeader(player.name, key)) : ''} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.[key]?.get(player.name.toLowerCase())) : ''}`}>
                                      <div className="flex flex-col items-center">
                                        <span>
                                          {leaderboardMode === 'finish' ? (() => {
                                            const info = RANKINGS?.[key]?.get(player.name.toLowerCase());
                                            if (!info) return '-';
                                            return `${info.isTie ? '=' : ''}${info.rank}`;
                                          })() : (() => {
                                            const val = row[key] ?? player[key] ?? 0;
                                            const displayVal = handicapEnabled ? val + getPlayerHandicap('leaderboard', player.name) : val;
                                            return typeof displayVal === 'number' ? displayVal.toFixed(2) : displayVal;
                                          })()}
                                        </span>
                                        {handicapEnabled && isHandicapLeader(player.name, key) && (
                                          <LeaderCheck className="w-2.5 h-2.5 mt-0.5" />
                                        )}
                                      </div>
                                    </td>
                                  ))}
                                  {/* Finish extras */}
                                  {leaderboardMode === 'finish' && (() => {
                                    const lower = player.name.toLowerCase();
                                    const limits = [3,5,10,20];
                                    const sources = ['byb','espn','betfair','count1','count2','count3','count4','count5'];
                                    const enabledSources = sources.filter((s) => enabledSourceSet.has(s));
                                    const denom = enabledSources.length || 1;
                                    // Dead-heat place shares: a tie straddling the
                                    // Top-N cut-off splits the remaining places.
                                    const countTopHits = (limit) => {
                                      let hits = 0;
                                      for (const key of enabledSources) {
                                        const info = RANKINGS?.[key]?.get(lower);
                                        if (info) hits += deadHeatPlaceShare(info.rank, info.tieCount, limit);
                                      }
                                      return hits;
                                    };
                                    const boxes = limits.map((L) => ({ L, hits: countTopHits(L) }));
                                    const firstAllAll = boxes.find((x) => Math.abs(x.hits - denom) < 1e-9)?.L ?? null;
                                    const renderCell = (L, hits) => {
                                      if (firstAllAll !== null && L > firstAllAll) {
                                        return <span className="text-slate-600 text-[10px] font-mono">N/A</span>;
                                      }
                                      const hitPct = (hits / denom) * 100;
                                      if (hitPct === 0) {
                                        return <span className="text-slate-600 text-[10px] font-mono">N/A</span>;
                                      }
                                      const odds = (1 / (hitPct / 100)).toFixed(2);
                                      return (
                                        <div className="flex flex-col items-center justify-center gap-1">
                                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-[11px] leading-none">{odds}</span>
                                          <span className="text-slate-500 text-[9px] font-mono tabular-nums">{formatWins(hits)}/{denom} ({formatPct(hitPct)}%)</span>
                                        </div>
                                      );
                                    };
                                    return boxes.map(({ L, hits }) => (
                                      <td key={`fv-${L}`} className="text-center px-2 py-2 text-[11px] text-slate-300 font-mono tabular-nums whitespace-nowrap">{renderCell(L, hits)}</td>
                                    ));
                                  })()}
                                  {/* X+ extras */}
                                  {leaderboardMode === 'xplus' && (() => {
                                    const thresholds = [30,25,20,15,10];
                                    const rowData = row;
                                    const getVal = (key) => {
                                      const v = rowData[key];
                                      return typeof v === 'number' ? v : 0;
                                    };
                                    const sources = ['byb','espn','betfair','count1','count2','count3','count4','count5'];
                                    const enabledSources = sources.filter((s) => enabledSourceSet.has(s));
                                    const denom = enabledSources.length || 1;
                                    const hitsByThreshold = thresholds.map((T) => {
                                      let hits = 0;
                                      for (const key of enabledSources) { if (getVal(key) >= T) hits += 1; }
                                      return { T, hits };
                                    });
                                    let seenAllEnabled = false;
                                    return hitsByThreshold.map(({ T, hits }) => {
                                      let content;
                                      if (seenAllEnabled) {
                                        content = <span className="text-slate-600 text-[10px] font-mono">N/A</span>;
                                      } else {
                                        const hitPct = (hits / denom) * 100;
                                        if (hitPct === 0) {
                                          content = <span className="text-slate-600 text-[10px] font-mono">N/A</span>;
                                        } else {
                                          const odds = (1 / (hitPct / 100)).toFixed(2);
                                          content = (
                                            <div className="flex flex-col items-center justify-center gap-1">
                                              <span className="text-emerald-300 font-mono font-bold tabular-nums text-[11px] leading-none">{odds}</span>
                                              <span className="text-slate-500 text-[9px] font-mono tabular-nums">{hits}/{denom} ({formatPct(hitPct)}%)</span>
                                            </div>
                                          );
                                        }
                                        if (hits === denom) seenAllEnabled = true;
                                      }
                                      return (
                                        <td key={`xv-${T}`} className="text-center px-2 py-2 text-[11px] text-slate-300 font-mono tabular-nums whitespace-nowrap">{content}</td>
                                      );
                                    });
                                  })()}
                                  {/* Implied odds when filtered */}
                                  {isFiltered && (() => {
                                    // Dead-heat adjusted: a tied count contributes 1/n.
                                    let leaderHits = 0;
                                    const denom = enabledIndices.length || 1;
                                    for (const idx of enabledIndices) leaderHits += filteredWinShare(player.name, idx);
                                    const hitPct = (leaderHits / denom) * 100;
                                    if (hitPct === 0) {
                                      return (
                                        <td className="text-center px-2 py-2 text-[11px] text-slate-300 font-mono tabular-nums whitespace-nowrap">N/A</td>
                                      );
                                    }
                                    const odds = (1 / (hitPct / 100)).toFixed(2);
                                    return (
                                      <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">
                                        <div className="flex flex-col items-center justify-center gap-1">
                                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-[11px] leading-none">{odds}</span>
                                          <span className="text-slate-500 text-[9px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom} ({formatPct(hitPct)}%)</span>
                                        </div>
                                      </td>
                                    );
                                  })()}
                                  {/* Handicap implied odds */}
                                  {handicapEnabled && (() => {
                                    // Dead-heat adjusted handicap wins.
                                    let leaderHits = 0;
                                    const denom = enabledDisplayKeys.length || 1;
                                    for (const key of enabledDisplayKeys) leaderHits += handicapWinShare(player.name, key);
                                    
                                    const hitPct = (leaderHits / denom) * 100;
                                    if (hitPct === 0) {
                                      return (
                                        <td className="text-center px-2 py-2 text-[11px] text-slate-300 font-mono tabular-nums whitespace-nowrap">N/A</td>
                                      );
                                    }
                                    const odds = (1 / (hitPct / 100)).toFixed(2);
                                    return (
                                      <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">
                                        <div className="flex flex-col items-center justify-center gap-1">
                                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-[11px] leading-none">{odds}</span>
                                          <span className="text-slate-500 text-[9px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom} ({formatPct(hitPct)}%)</span>
                                        </div>
                                      </td>
                                    );
                                  })()}
                                  {/* Vote Frequency averages */}
                                  {voteFrequencyEnabled && FREQ_STATS && (() => {
                                    const lower = player.name.toLowerCase();
                                    const getCounts = (cat) => {
                                      const arr = FREQ_STATS[cat];
                                      const ordered = [3,4,5,6,7,2,1,0].map((idx) => arr[idx]);
                                      const keysOrdered = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                                      const filtered = ordered.map((pack, i) => ({ pack, key: keysOrdered[i] }))
                                        .filter(({ key }) => enabledSourceSet.has(key));
                                      const values = filtered.map(({ pack }) => {
                                        const rec = pack.freqByPlayer.get(lower);
                                        return rec ? (rec[cat] || 0) : 0;
                                      });
                                      const denom = filtered.reduce((a, { pack }) => a + (pack.freqByPlayer.has(lower) ? 1 : 0), 0);
                                      const avg = denom > 0 ? (values.reduce((a,b)=>a+b,0) / denom) : 0;
                                      return denom > 0 ? avg.toFixed(1) : 'N/A';
                                    };
                                    const items = ['three','two','one','polled'];
                                    const renderAggPill = (val) => (
                                      <span className="inline-block text-emerald-300 font-semibold font-mono tabular-nums text-xs min-w-[2.5rem] text-center">{val}</span>
                                    );
                                    return items.map((key) => (
                                      <td key={`vf-${key}`} className="text-center px-2 py-2 text-[11px] text-slate-300 font-mono tabular-nums whitespace-nowrap">{renderAggPill(getCounts(key))}</td>
                                    ));
                                  })()}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop: full table remains unchanged */}
      <div className="hidden md:block rounded-2xl border border-slate-800 bg-[#0a1120] overflow-hidden">
        <div className="overflow-x-auto styled-scrollbar">
          <table className="w-full md:min-w-[800px]">
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className={`${STICKY_HEAD} md:left-0 text-left px-2 md:px-4 py-2.5 w-[68px] text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]`}>
                  POS
                </th>
                <th className={`${STICKY_HEAD} md:left-[64px] text-left px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] min-w-[200px] border-r border-slate-800/70`}>
                  PLAYER
                </th>
                {enabledDisplayKeys.includes('count1') && (
                  <th onClick={() => setSortKey('count1')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BG 1 {sortKey === 'count1' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('count2') && (
                  <th onClick={() => setSortKey('count2')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BG 2 {sortKey === 'count2' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('count3') && (
                  <th onClick={() => setSortKey('count3')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BG 3 {sortKey === 'count3' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('count4') && (
                  <th onClick={() => setSortKey('count4')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BG 4 {sortKey === 'count4' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('count5') && (
                  <th onClick={() => setSortKey('count5')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BG 5 {sortKey === 'count5' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('betfair') && (
                  <th onClick={() => setSortKey('betfair')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BETFAIR {sortKey === 'betfair' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('espn') && (
                  <th onClick={() => setSortKey('espn')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    ESPN {sortKey === 'espn' && <SortCaret />}
                  </th>
                )}
                {enabledDisplayKeys.includes('byb') && (
                  <th onClick={() => setSortKey('byb')} className="hidden md:table-cell text-center px-1 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors">
                    BYB {sortKey === 'byb' && <SortCaret />}
                  </th>
                )}
                {/* Top finish columns */}
                {leaderboardMode === 'finish' && (
                  <>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Top 3<ImpliedOddsInfo /></span></th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Top 5<ImpliedOddsInfo /></span></th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Top 10<ImpliedOddsInfo /></span></th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Top 20<ImpliedOddsInfo /></span></th>
                  </>
                )}
                {leaderboardMode === 'xplus' && (
                  <>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">30+ Votes</th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">25+ Votes</th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">20+ Votes</th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">15+ Votes</th>
                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">10+ Votes</th>
                  </>
                )}
                {handicapEnabled && (
                  <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">HANDICAP</th>
                )}
                <th onClick={() => setSortKey('votes')} className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-semibold text-emerald-300 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-emerald-200 transition-colors bg-emerald-500/10">
                  VOTES {sortKey === 'votes' && <SortCaret />}
                </th>
                {handicapEnabled && (
                  <th className="text-center px-1 md:px-2 py-2.5 w-20 md:w-28 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                )}
              {(isFiltered) && (
                <th className="text-center px-1 md:px-2 py-2.5 w-20 md:w-28 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
              )}
              {voteFrequencyEnabled && (
                  <>
                    <th
                      onClick={() => setSortKey('vf_3v')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors border-l border-slate-800/70"
                      title={`3V threshold: ≥ ${(freqThresholds?.threeMin ?? 2.4).toFixed(1)} votes`}
                    >
                      3V {sortKey === 'vf_3v' && <SortCaret />}
                    </th>
                    <th
                      onClick={() => setSortKey('vf_2v')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                      title={`2V threshold: ≥ ${(freqThresholds?.twoMin ?? 1.4).toFixed(1)} and < ${(freqThresholds?.twoMax ?? 2.4).toFixed(1)}`}
                    >
                      2V {sortKey === 'vf_2v' && <SortCaret />}
                    </th>
                    <th
                      onClick={() => setSortKey('vf_1v')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                      title={`1V threshold: ≥ ${(freqThresholds?.oneMin ?? 0.7).toFixed(1)} and < ${(freqThresholds?.oneMax ?? 1.4).toFixed(1)}`}
                    >
                      1V {sortKey === 'vf_1v' && <SortCaret />}
                    </th>
                    <th
                      onClick={() => setSortKey('vf_gp')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                      title={`GP threshold: ≥ ${(freqThresholds?.polledMin ?? 0.7).toFixed(1)} votes`}
                    >
                      GP {sortKey === 'vf_gp' && <SortCaret />}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player, index) => {
                const playerImageSrc = getPlayerImage(player.name);
                const row = filteredLeaderboard.find((p) => p.name === player.name) || player;
                const displayPosition = votesRankByName.get(player.name.toLowerCase()) ?? player.position;
              // Effective wins (dead-heat adjusted) across enabled datasets,
              // using handicap leaders when that mode is on, else filtered leaders.
              const leaderHits = computeLeaderHits(player.name);
              const denom = enabledDisplayKeys.length || 1;
                return (
                  <tr 
                    key={player.name}
                    className="group/row border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors"
                  >
                    {/* Position */}
                    <td className={`${STICKY_CELL} md:left-0 px-2 md:px-4 py-1.5 w-[68px]`}>
                      <span className={`inline-flex items-center justify-center min-w-[26px] h-[22px] px-1.5 rounded-md border text-[11px] font-mono font-semibold tabular-nums ${rankBadgeClass(displayPosition)}`}>
                        {displayPosition}
                      </span>
                    </td>

                    {/* Player */}
                    <td className={`${STICKY_CELL} md:left-[64px] px-2 md:px-4 py-1.5 min-w-[200px] border-r border-slate-800/70`}>
                      <div className="flex items-center space-x-2 md:space-x-3">
                        {playerImageSrc ? (
                          <img 
                            src={playerImageSrc} 
                            alt={player.name}
                            className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                          />
                        ) : (
                          <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                            <span className="text-white font-bold text-[10px]">{player.image}</span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div 
                            className="flex items-center gap-1.5 min-w-0 text-white font-semibold cursor-pointer hover:text-emerald-300 transition-colors text-xs md:text-[13px]"
                            onClick={() => openPlayerModal(row)}
                            title="View vote breakdown"
                          >
                            <span className="truncate">{player.name}</span>
                            <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">{player.team}</div>
                        </div>
                      </div>
                    </td>

                    {/* Count columns - Hidden on mobile; also hidden when dataset disabled */}
                    {enabledDisplayKeys.includes('count1') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'count1') : isLeaderForSource(player.name, 3)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count1 ?? player.count1) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.count1?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.count1?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.count1 ?? player.count1) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.count1 ?? player.count1).toFixed ? (row.count1).toFixed(2) : row.count1 ?? player.count1))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'count1') : isLeaderForSource(player.name, 3)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count1 ?? player.count1) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}
                    {enabledDisplayKeys.includes('count2') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'count2') : isLeaderForSource(player.name, 4)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count2 ?? player.count2) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.count2?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.count2?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.count2 ?? player.count2) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.count2 ?? player.count2).toFixed ? (row.count2).toFixed(2) : row.count2 ?? player.count2))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'count2') : isLeaderForSource(player.name, 4)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count2 ?? player.count2) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}
                    {enabledDisplayKeys.includes('count3') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'count3') : isLeaderForSource(player.name, 5)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count3 ?? player.count3) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.count3?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.count3?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.count3 ?? player.count3) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.count3 ?? player.count3).toFixed ? (row.count3).toFixed(2) : row.count3 ?? player.count3))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'count3') : isLeaderForSource(player.name, 5)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count3 ?? player.count3) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}
                    {enabledDisplayKeys.includes('count4') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'count4') : isLeaderForSource(player.name, 6)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count4 ?? player.count4) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.count4?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.count4?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.count4 ?? player.count4) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.count4 ?? player.count4).toFixed ? (row.count4).toFixed(2) : row.count4 ?? player.count4))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'count4') : isLeaderForSource(player.name, 6)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count4 ?? player.count4) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}
                    {enabledDisplayKeys.includes('count5') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'count5') : isLeaderForSource(player.name, 7)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count5 ?? player.count5) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.count5?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.count5?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.count5 ?? player.count5) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.count5 ?? player.count5).toFixed ? (row.count5).toFixed(2) : row.count5 ?? player.count5))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'count5') : isLeaderForSource(player.name, 7)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.count5 ?? player.count5) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}

                    {/* External columns - Hidden on mobile; also hidden when dataset disabled */}
                    {enabledDisplayKeys.includes('betfair') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'betfair') : isLeaderForSource(player.name, 2)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.betfair ?? player.betfair) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.betfair?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.betfair?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.betfair ?? player.betfair) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.betfair ?? player.betfair).toFixed ? (row.betfair).toFixed(2) : row.betfair ?? player.betfair))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'betfair') : isLeaderForSource(player.name, 2)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.betfair ?? player.betfair) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}
                    {enabledDisplayKeys.includes('espn') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'espn') : isLeaderForSource(player.name, 1)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.espn ?? player.espn) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.espn?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.espn?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.espn ?? player.espn) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.espn ?? player.espn).toFixed ? (row.espn).toFixed(2) : row.espn ?? player.espn))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'espn') : isLeaderForSource(player.name, 1)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.espn ?? player.espn) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}
                    {enabledDisplayKeys.includes('byb') && (
                      <td className={`hidden md:table-cell px-1 md:px-4 py-1.5 text-center ${leaderCellClass((handicapEnabled ? isHandicapLeader(player.name, 'byb') : isLeaderForSource(player.name, 0)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.byb ?? player.byb) >= xplusHover.threshold))} ${leaderboardMode === 'finish' ? finishBgClass(RANKINGS?.byb?.get(player.name.toLowerCase())) : ''}`}>
                        <div className="flex flex-col items-center">
                          <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">
                            {leaderboardMode === 'finish' ? (() => {
                              const info = RANKINGS?.byb?.get(player.name.toLowerCase());
                              if (!info) return '-';
                              return `${info.isTie ? '=' : ''}${info.rank}`;
                            })() : (handicapEnabled ? ((row.byb ?? player.byb) + getPlayerHandicap('leaderboard', player.name)).toFixed(2) : ((row.byb ?? player.byb).toFixed ? (row.byb).toFixed(2) : row.byb ?? player.byb))}
                          </span>
                          {((handicapEnabled ? isHandicapLeader(player.name, 'byb') : isLeaderForSource(player.name, 0)) || (leaderboardMode === 'xplus' && xplusHover.name === player.name && xplusHover.threshold !== null && (row.byb ?? player.byb) >= xplusHover.threshold)) && (
                            <LeaderCheck className="w-3 h-3 mt-1" />
                          )}
                        </div>
                      </td>
                    )}

                    {/* Top finish cells */}
                    {leaderboardMode === 'finish' && (() => {
                      const sources = ['byb', 'espn', 'betfair', 'count1', 'count2', 'count3', 'count4', 'count5'];
                      const enabledSources = sources.filter((s) => enabledSourceSet.has(s));
                      const denomLocal = enabledSources.length || 1;
                      const lower = player.name.toLowerCase();
                      const limits = [3, 5, 10, 20];
                      // Dead-heat place shares: a tie straddling the Top-N
                      // cut-off splits the remaining places across the group.
                      const countTopHits = (limit) => {
                        let hits = 0;
                        for (const key of enabledSources) {
                          const info = RANKINGS?.[key]?.get(lower);
                          if (info) hits += deadHeatPlaceShare(info.rank, info.tieCount, limit);
                        }
                        return hits;
                      };
                      const hitsByLimit = limits.map((L) => ({ L, hits: countTopHits(L) }));
                      const firstAllAll = hitsByLimit.find((x) => Math.abs(x.hits - denomLocal) < 1e-9)?.L ?? null;
                       const renderTopCell = (limit) => {
                        const entry = hitsByLimit.find((x) => x.L === limit);
                        const hits = entry ? entry.hits : 0;
                        if (firstAllAll !== null && limit > firstAllAll) {
                          return (
                            <td className="px-2 md:px-4 py-1.5 text-center">
                              <span className="text-slate-600 text-[11px] font-mono">N/A</span>
                            </td>
                          );
                        }
                        const hitPct = (hits / denomLocal) * 100;
                         if (hitPct === 0) {
                           return (
                             <td className="px-2 md:px-4 py-1.5 text-center">
                               <span className="text-slate-600 text-[11px] font-mono">N/A</span>
                             </td>
                           );
                         }
                          const odds = (1 / (hitPct / 100)).toFixed(2);
                          return (
                            <td className="px-2 md:px-4 py-1.5 text-center">
                              <div className="inline-flex items-center justify-center gap-2 md:flex-col md:gap-0">
                                <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px] leading-none">{odds}</span>
                                <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(hits)}/{denomLocal} ({formatPct(hitPct)}%)</span>
                              </div>
                            </td>
                          );
                      };
                      return (
                        <>
                          {renderTopCell(3)}
                          {renderTopCell(5)}
                          {renderTopCell(10)}
                          {renderTopCell(20)}
                        </>
                      );
                    })()}

                    {/* X+ Votes threshold cells */}
                    {leaderboardMode === 'xplus' && (() => {
                      const sources = ['byb', 'espn', 'betfair', 'count1', 'count2', 'count3', 'count4', 'count5'];
                      const enabledSources = sources.filter((s) => enabledSourceSet.has(s));
                      const denomLocal = enabledSources.length || 1;
                      const thresholds = [30, 25, 20, 15, 10]; // evaluate high to low
                      const rowData = filteredLeaderboard.find((p) => p.name === player.name) || player;
                      const getVal = (key) => {
                        const v = rowData[key];
                        return typeof v === 'number' ? v : 0;
                      };
                      const hitsByThreshold = thresholds.map((T) => {
                        let hits = 0;
                        for (const key of enabledSources) {
                          if (getVal(key) >= T) hits += 1;
                        }
                        return { T, hits };
                      });
                      let seenAllEnabled = false;
                      const renderThresholdCell = (T) => {
                        const entry = hitsByThreshold.find((x) => x.T === T);
                        const hits = entry ? entry.hits : 0;
                        let content;
                        if (seenAllEnabled) {
                          content = <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                        } else {
                          if (hits === denomLocal) seenAllEnabled = true;
                          const hitPct = (hits / denomLocal) * 100;
                          if (hitPct === 0) {
                            content = <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                          } else {
                            const odds = (1 / (hitPct / 100)).toFixed(2);
                            content = (
                              <div className="inline-flex items-center justify-center gap-2 md:flex-col md:gap-0">
                                <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px] leading-none">{odds}</span>
                                <span className="text-slate-500 text-[10px] font-mono tabular-nums">{hits}/{denomLocal} ({formatPct(hitPct)}%)</span>
                              </div>
                            );
                          }
                        }
                        return (
                          <td
                            className="px-2 md:px-4 py-1.5 text-center"
                            key={`${player.name}-thr-${T}`}
                            onMouseEnter={() => setXplusHover({ name: player.name, threshold: T })}
                            onMouseLeave={() => setXplusHover({ name: null, threshold: null })}
                          >
                            {content}
                          </td>
                        );
                      };
                      return (
                        <>
                          {renderThresholdCell(30)}
                          {renderThresholdCell(25)}
                          {renderThresholdCell(20)}
                          {renderThresholdCell(15)}
                          {renderThresholdCell(10)}
                        </>
                      );
                    })()}
                    {/* Handicap input */}
                    {handicapEnabled && (
                      <td className="px-2 md:px-4 py-1.5 text-center">
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="0.0"
                          value={getDisplayHandicapValue('leaderboard', player.name)}
                          onChange={(e) => updateTempHandicapInput('leaderboard', player.name, e.target.value)}
                          onBlur={(e) => applyPlayerHandicap('leaderboard', player.name, e.target.value)}
                          className="w-16 bg-slate-950/60 border border-slate-700/60 text-white font-mono tabular-nums rounded-md text-[13px] px-2 py-1 text-center focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/20 transition-colors"
                        />
                      </td>
                    )}
                    {/* Votes - dynamic average of enabled datasets */}
                    <td className="px-2 md:px-4 py-1.5 text-center bg-emerald-500/[0.06]">
                      <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm md:text-[15px]">{averageEnabledFromRow(row).toFixed(2)}</span>
                    </td>
                    {handicapEnabled && (
                      <td className="px-1 md:px-2 py-1.5 text-center w-20 md:w-28">
                        {(() => {
                          const hitPct = (leaderHits / denom) * 100;
                          if (hitPct === 0) {
                            return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                          }
                          const odds = (1 / (hitPct / 100)).toFixed(2);
                          return (
                            <div className="flex flex-col items-center justify-center gap-1">
                              <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px] leading-none">{odds}</span>
                              <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom} ({formatPct(hitPct)}%)</span>
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {isFiltered && (
                      <td className="px-1 md:px-2 py-1.5 text-center w-20 md:w-28">
                        {(() => {
                          const hitPct = (leaderHits / denom) * 100;
                          if (hitPct === 0) {
                            return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                          }
                          const odds = (1 / (hitPct / 100)).toFixed(2);
                          return (
                            <div className="flex flex-col items-center justify-center gap-1">
                              <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px] leading-none">{odds}</span>
                              <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom} ({formatPct(hitPct)}%)</span>
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {voteFrequencyEnabled && FREQ_STATS && (() => {
                      const lower = player.name.toLowerCase();
                      // Each entry keeps its own label so the popover stays
                      // aligned when disabled datasets are filtered out.
                      const shortLabels = { count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', betfair: 'BET', espn: 'ESPN', byb: 'BYB' };
                      const getPerSourceCounts = (cat) => {
                        const arr = FREQ_STATS[cat];
                        const ordered = [3,4,5,6,7,2,1,0].map((idx) => arr[idx]);
                        const keysOrdered = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
                        const filtered = ordered.map((pack, i) => ({ pack, key: keysOrdered[i] }))
                          .filter(({ key }) => enabledSourceSet.has(key));
                        return filtered.map(({ pack, key }) => {
                          const { freqByPlayer } = pack;
                          const has = freqByPlayer.has(lower);
                          const rec = freqByPlayer.get(lower) || {};
                          const val = has ? (rec[cat] || 0) : 0;
                          return { val, present: has, label: shortLabels[key] };
                        });
                      };
                      const renderAggWithPopover = (counts) => {
                        const total = counts.reduce((a, b) => a + (b.val || 0), 0);
                        const denom = counts.reduce((a, b) => a + (b.present ? 1 : 0), 0);
                        const avg = denom > 0 ? (total / denom) : 0;
                        return (
                          <div className="relative inline-block group">
                            <div className="inline-flex items-center justify-center">
                              <span className="inline-block text-emerald-300 font-semibold font-mono tabular-nums text-xs md:text-[13px] min-w-[2.5rem] text-center border-b border-dashed border-emerald-500/25 group-hover:border-emerald-400/60 transition-colors">{denom > 0 ? avg.toFixed(1) : 'N/A'}</span>
                            </div>
                            <div className="hidden group-hover:block group-focus-within:block absolute left-1/2 -translate-x-1/2 mt-2 z-40 w-44 md:w-48">
                              <div className="bg-slate-900 border border-slate-700/70 rounded-xl shadow-2xl shadow-black/60 p-2">
                                <div className="grid grid-cols-4 gap-1">
                                  {counts.map((entry, idx) => (
                                    <div key={idx} className={`flex flex-col items-center justify-center rounded p-1 ${entry.present ? 'bg-slate-800' : 'bg-slate-800/40'}`} title={`${entry.label}: ${entry.val}${entry.present ? '' : ' (missing)'}`}>
                                      <div className="text-[9px] md:text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 leading-none">{entry.label}</div>
                                      <div className={`text-[11px] md:text-xs font-mono tabular-nums leading-tight ${entry.present ? 'text-slate-200' : 'text-slate-600'}`}>{entry.val}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      };
                      const polledCounts = getPerSourceCounts('polled');
                      const threeCounts = getPerSourceCounts('three');
                      const twoCounts = getPerSourceCounts('two');
                      const oneCounts = getPerSourceCounts('one');
                      return (
                        <>
                          <td className="px-1 md:px-2 py-1.5 text-center border-l border-slate-800/70">{renderAggWithPopover(threeCounts)}</td>
                          <td className="px-1 md:px-2 py-1.5 text-center">{renderAggWithPopover(twoCounts)}</td>
                          <td className="px-1 md:px-2 py-1.5 text-center">{renderAggWithPopover(oneCounts)}</td>
                          <td className="px-1 md:px-2 py-1.5 text-center">{renderAggWithPopover(polledCounts)}</td>
                        </>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Player Vote Modal */}
      <DetailedPlayerVoteModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        player={selectedPlayer}
        playerImage={selectedPlayer ? getPlayerImage(selectedPlayer.name) : null}
        gameVoteData={voteDataRemote || null}
      />
    </div>
  );
};

export default PlayerLeaderboardTable;